import React, { useMemo, useState } from 'react';

import { dashboardCustomsInstructionApis } from '@apis/next/customsInstructionApis';
import { dashboardCustomsRequestApis } from '@apis/next/customsRequestApis';
import { dashboardPdfApis } from '@apis/next/pdfApis';
import { CustomsRequestFormDialog } from '@components/admin/booking/CustomsRequestFormDialog';
import { CustomsRequestSubmitDialog } from '@components/admin/booking/CustomsRequestSubmitDialog';
import { CustomsRequestURLSubmitDialog } from '@components/admin/booking/CustomsRequestURLSubmitDialog';
import { Button } from '@components/Button';
import { Layout } from '@components/Layout';
import { customsRequestStrings } from '@data/customsRequest';
import { useSearch } from '@hooks/next/useSearch';
import { useConfirmDialog } from '@hooks/useActionableDialog';
import { useDialogKey } from '@hooks/useDialogKey';
import { CustomsRequestHelper } from '@models/next/CustomsRequest';
import { UserHelper } from '@models/User';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { foldMessagesEither } from '@utils/errors';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { mapOptional, toJST } from '@utils/types';
import clsx from 'clsx';
import { Document, Page } from 'react-pdf';
import { z } from 'zod';

const strings = createStrings({
  ja: {
    title: `${customsRequestStrings.customsRequest}`,
    exportPdf: 'PDF出力',
    sentMessage: '送信しました。',
    additionalSentMessage: '追加送信しました。',
    submit: '通関依頼',
    copyLink: 'リンクをコピー',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  background: {
    backgroundColor: theme.palette.background.paper,
  },
  container: {
    ...common.flexCenter,
    margin: 16,
    marginBottom: 200,
  },
  operationPanel: {
    ...common.flexCenter,
    ...common.smallerText,
    position: 'fixed',
    bottom: 0,
    background: theme.palette.primary.contrastText,
    padding: '10px 45px',
    width: '100%',
    boxShadow: '0px -4px 4px rgba(0, 0, 0, 0.25);',
  },
  operationBox: {
    ...common.flexInline,
    ...common.smallerText,
    background: theme.palette.primary.contrastText,
    padding: '10px 45px',
    maxWidth: 960,
    width: '100%',
  },
  info: {
    flex: 1,
    ...common.flexColumn,
  },
  operation: {
    ...common.flexCenter,
    columnGap: 30,
  },
}));

export const CustomsRequestDetails: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { search } = useSearch(
    z.object({
      d2d_no: z.string(),
    }),
  );

  const { renderDialog, confirm } = useConfirmDialog();

  const [openEdit, setOpenEdit] = useState(false);
  const [openSubmit, setOpenSubmit] = useState(false);
  const [openURLSubmit, setOpenURLSubmit] = useState(false);

  const openURLKey = useDialogKey(openURLSubmit);

  const customsRequestQuery = dashboardCustomsRequestApis.useGet(
    '/dashboard/customs-request/:d2d_no',
    {
      params: {
        d2d_no: search.d2d_no,
      },
    },
  );

  const customsInstructionQuery = dashboardCustomsInstructionApis.useGet(
    '/dashboard/customs-instruction/:d2d_no',
    {
      params: {
        d2d_no: search.d2d_no,
      },
    },
  );

  const pdfCustomsRequestQuery = dashboardPdfApis.useGet(
    '/dashboard/pdf/CustomsRequest/:d2d_no',
    {
      params: { d2d_no: search.d2d_no },
    },
    {
      staleTime: Infinity,
    },
  );

  const pdfUrl = useMemo(
    () =>
      pdfCustomsRequestQuery.data ? URL.createObjectURL(pdfCustomsRequestQuery.data) : undefined,
    [pdfCustomsRequestQuery.data],
  );

  const helper = customsRequestQuery.data && new CustomsRequestHelper(customsRequestQuery.data);

  const renderOperationPanel = () => {
    return (
      <div className={classes.operationPanel}>
        <div className={classes.operationBox}>
          <div className={classes.info}>
            {mapOptional(helper?.getSubmissionCount(), (count) => (
              <div className={clsx(classes.textPrimary, classes.fontWeight600, classes.middleText)}>
                {count === 1 ? strings.sentMessage : count > 1 ? strings.additionalSentMessage : ''}
              </div>
            ))}

            <div>
              {customsRequestStrings.createdAt}
              {': '}
              {customsRequestQuery.data?.created_at
                ? toJST(new Date(customsRequestQuery.data.created_at))
                : '-'}
            </div>

            <div>
              {customsRequestStrings.creator}:{' '}
              {mapOptional(customsRequestQuery.data?.creator, (u) => new UserHelper(u).fullname) ??
                '-'}
            </div>

            <div>
              {customsRequestStrings.submittedAt}:{' '}
              {mapOptional(helper, (h) => h.getSubmittedAt()) ?? '-'}
            </div>

            <div>
              {customsRequestStrings.submissionTarget}:{' '}
              {mapOptional(customsRequestQuery.data?.submission_target?.company, (company) => (
                <>
                  {company}
                  &nbsp;&nbsp;
                  <a className={classes.link} onClick={() => setOpenSubmit(true)}>
                    {formStrings.change}
                  </a>
                </>
              )) ?? '-'}
            </div>

            <div>
              <a
                href={url('/admin/booking/PrintCustomsRequestLog') + `?d2d_no=${search.d2d_no}`}
                target="_blank"
                className={classes.link}
              >
                {customsRequestStrings.changeLog}
              </a>
            </div>
          </div>

          <div className={classes.operation}>
            <Button
              color="primary"
              text={formStrings.edit}
              type="button"
              shape="round"
              size="small"
              onClick={() => {
                setOpenEdit(true);
              }}
            />

            <Button
              color="primary"
              text={strings.exportPdf}
              type="button"
              shape="round"
              size="small"
              onClick={async () => {
                if (pdfUrl) {
                  window.open(pdfUrl);
                }
              }}
            />

            <Button
              color={
                helper?.canSubmit()._tag === 'Right'
                  ? helper.getSubmissionCount() >= 1
                    ? 'accent'
                    : 'primary'
                  : 'grey'
              }
              text={strings.submit}
              type="button"
              shape="round"
              size="small"
              onClick={() => {
                helper &&
                  foldMessagesEither(
                    helper.canSubmit(),
                    (e) => {
                      confirm({
                        message: e.messages,
                      });
                    },
                    () => {
                      setOpenSubmit(true);
                    },
                  );
              }}
            />

            <Button
              color={
                helper?.canSubmit()._tag === 'Right'
                  ? helper.getSubmissionCount() >= 1
                    ? 'accent'
                    : 'primary'
                  : 'grey'
              }
              text={strings.copyLink}
              type="button"
              shape="round"
              size="small"
              onClick={() => {
                helper &&
                  foldMessagesEither(
                    helper.canSubmit(),
                    (e) => {
                      confirm({
                        message: e.messages,
                      });
                    },
                    () => {
                      setOpenURLSubmit(true);
                    },
                  );
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <Layout
      hideHeader
      hideFooter
      hideCookiePolicy
      title={`${strings.title} - ${search.d2d_no}`}
      loading={
        customsRequestQuery.isLoading ||
        pdfCustomsRequestQuery.isLoading ||
        customsInstructionQuery.isLoading
      }
      errors={[
        customsRequestQuery.error,
        pdfCustomsRequestQuery.error,
        customsInstructionQuery.error,
      ]}
      classes={{
        background: classes.background,
      }}
    >
      {renderDialog()}

      <div className={classes.container}>
        <Document key={pdfUrl} file={pdfUrl} loading="">
          <Page pageNumber={1} width={900} />
        </Document>
      </div>

      {renderOperationPanel()}
      <CustomsRequestFormDialog
        type="update"
        open={openEdit}
        onClose={() => {
          setOpenEdit(false);
        }}
        d2dNo={search.d2d_no}
        customsInstruction={customsInstructionQuery.data!}
        customsRequest={customsRequestQuery.data!}
      />

      <CustomsRequestSubmitDialog
        open={openSubmit}
        onClose={() => setOpenSubmit(false)}
        customsRequest={customsRequestQuery.data!}
      />

      {customsRequestQuery.data && (
        <CustomsRequestURLSubmitDialog
          key={openURLKey}
          open={openURLSubmit}
          onClose={() => setOpenURLSubmit(false)}
          customsRequest={customsRequestQuery.data}
        />
      )}
    </Layout>
  );
};
